import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Search, Folder, Layers, Video, AudioLines, Image, FileText } from 'lucide-react';
import { selectCurrentGroupId } from '@/store/selectors';
import { useSession } from '@/hooks/useSession';
import searchItems from '@/apiServices/searchApi';
import { cutItemTitle } from '@/util/FitTextIntoWidth';
import NotificationContext from '@/context/notification';
import { NavDropdown } from './NavDropdown';

interface NavSearchProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

interface SearchResult {
  id: { S: string };
  title?: { S: string };
  name?: { S: string };
  itemtype: { S: string };
  parent?: { S: string };
  parentType?: { S: string };
  type?: { S: string };
}

export function NavSearch({ open, onToggle, onClose }: NavSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [status, setStatus] = useState<'idle' | 'typing' | 'loading' | 'done'>('idle');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const groupId = useSelector(selectCurrentGroupId);
  const { updateIsQuerying } = useSession();
  const notifications = useContext(NotificationContext) as {
    add: (n: { id: number; type: string; message: string }) => void;
  } | null;

  const reset = useCallback(() => {
    setQuery('');
    setResults([]);
    setStatus('idle');
    setSelectedIndex(-1);
    onClose();
  }, [onClose]);

  const getItemUrl = (item: SearchResult) => {
    if (item.itemtype.S === 'resource') {
      return `/in/${item.parentType?.S}/${item.parent?.S}/resource/${item.id.S}/allfiles`;
    }
    return `/in/${item.itemtype.S}/${item.id.S}`;
  };

  const navigateToItem = useCallback(
    (item: SearchResult) => {
      navigate(getItemUrl(item));
      reset();
    },
    [navigate, reset],
  );

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!open) return;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev <= 0 ? results.length - 1 : prev - 1,
          );
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev >= results.length - 1 ? 0 : prev + 1,
          );
          break;
        case 'Enter':
          if (selectedIndex >= 0 && results[selectedIndex]) {
            navigateToItem(results[selectedIndex]);
          }
          break;
        case 'Escape':
          reset();
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, results, selectedIndex, navigateToItem, reset]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);

    if (value.length === 0) {
      reset();
      return;
    }

    if (value.length > 0 && !open) {
      onToggle();
    }

    if (value.length < 3) {
      setResults([]);
      setStatus('typing');
      return;
    }

    try {
      setStatus('loading');
      updateIsQuerying(true);
      const response = await searchItems(value);
      updateIsQuerying(false);
      setResults(response.response.Items);
      setStatus('done');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Search failed';
      notifications?.add({ id: Date.now(), type: 'error', message });
      setStatus('done');
      setResults([]);
      updateIsQuerying(false);
    }
  };

  const renderResults = () => {
    if (status === 'typing') {
      return (
        <div className="px-3 py-2 text-sm text-muted-foreground">
          Type at least 3 characters
        </div>
      );
    }

    if (status === 'loading') {
      return (
        <div className="space-y-1 px-3 py-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-8 animate-pulse rounded bg-secondary" />
          ))}
        </div>
      );
    }

    if (status === 'done' && results.length === 0) {
      return (
        <div className="px-3 py-2 text-sm text-muted-foreground">
          No results for &ldquo;{query}&rdquo;
        </div>
      );
    }

    return results.map((item, index) => {
      const title = cutItemTitle(
        item.title ? item.title.S : item.name?.S || '',
        185,
      );
      return (
        <button
          key={item.id.S}
          type="button"
          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
            index === selectedIndex
              ? 'bg-primary-muted text-primary-hover'
              : 'text-accent-foreground hover:bg-secondary'
          }`}
          onClick={() => navigateToItem(item)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <span className="flex-shrink-0 text-muted-foreground">
            {item.itemtype.S === 'folder' ? <Folder className="size-4" /> :
             item.itemtype.S === 'project' ? <Layers className="size-4" /> :
             item.type?.S === 'video' ? <Video className="size-4" /> :
             item.type?.S === 'audio' ? <AudioLines className="size-4" /> :
             item.type?.S === 'image' ? <Image className="size-4" /> :
             <FileText className="size-4" />}
          </span>
          <span className="truncate">{title}</span>
        </button>
      );
    });
  };

  return (
    <NavDropdown
      open={open}
      onClose={reset}
      onTriggerClick={onToggle}
      closeOnContentClick={false}
      align="left"
      className="w-80"
      trigger={<Search className="h-4 w-4 text-muted-foreground" />}
    >
      <div className="border-b border-border-light px-3 py-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={handleChange}
            placeholder="Search..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">{renderResults()}</div>
    </NavDropdown>
  );
}
